// eslint-disable-next-line no-restricted-imports
import { Amount as AmountGds } from '@george-labs.com/design-system';
import type {
    AMOUNT_COLOR,
    AmountProps,
} from '@george-labs.com/design-system/types/components/Amount';

import type { Amount } from '@shared/core-types';
import { getFeatureConfig } from '@luna/feature-config';
import { getFrontendUserConfiguration } from '@shared/user-configuration';

const PERCENT_SIGN = '%';
const CURRENCY_EUR = 'EUR';
const CURRENCY_USD = 'USD';
const CURRENCY_SYMBOL_MAP: Record<string, string> = {
    [CURRENCY_EUR]: '€',
    [CURRENCY_USD]: '$',
};

function isNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value);
}

export function ensureAmountObject(input: Amount | number): Amount {
    let amount: Partial<Amount> & { value: number };

    if (isNumber(input)) {
        amount = { value: +input };
    } else {
        amount = input as Amount;
    }

    const { defaultCurrency, defaultPrecision } = getFeatureConfig((config) => config.base);

    return {
        value: amount.value,
        precision: amount.precision ?? defaultPrecision,
        currency: amount.currency ?? defaultCurrency,
    };
}

export function getAmountColor(amount: Amount, redTransactions?: boolean): AMOUNT_COLOR {
    if (amount.value > 0) {
        return AmountGds.COLOR.POSITIVE;
    }

    if (amount.value < 0 && redTransactions) {
        return AmountGds.COLOR.NEGATIVE;
    }

    return AmountGds.COLOR.NEUTRAL;
}

export function isAppendedCurrency(currency: string): boolean {
    if (currency === PERCENT_SIGN) {
        return true;
    }

    const { defaultCurrency, defaultCurrencyPre, doPreCurrencyCheck, displayISOCurrency } =
        getFeatureConfig((config) => config.base);

    if (displayISOCurrency) {
        return true;
    }

    if (currency === defaultCurrency) {
        return !defaultCurrencyPre;
    }

    if ([CURRENCY_EUR, CURRENCY_USD].includes(currency)) {
        return false;
    }

    return !doPreCurrencyCheck;
}

export function getCurrencySymbol(currency?: string): string | undefined {
    const { defaultCurrency, defaultCurrencySign, displayISOCurrency } = getFeatureConfig(
        (config) => config.base,
    );

    if (displayISOCurrency) {
        return currency;
    }

    if (currency === defaultCurrency) {
        return defaultCurrencySign;
    }

    if (!currency) {
        return undefined;
    }

    return CURRENCY_SYMBOL_MAP[currency];
}

export function buildLocalizedAmountProps(
    amount: Amount,
): Pick<
    AmountProps,
    'decimalSeparator' | 'thousandSeparator' | 'isAppendedCurrency' | 'currencySymbol'
> {
    const { decimalSeparator, thousandSeparator } = getFeatureConfig((config) => config.base);

    return {
        decimalSeparator,
        thousandSeparator,
        isAppendedCurrency: isAppendedCurrency(amount.currency),
        currencySymbol: getCurrencySymbol(amount.currency),
    };
}

export function computeColorProp(
    amount: Amount,
    providedColor: AMOUNT_COLOR | undefined,
    coloured: boolean,
    showNegativeInRedColor?: boolean,
): AMOUNT_COLOR | undefined {
    const showRedTransactions =
        showNegativeInRedColor ??
        getFrontendUserConfiguration().userConfiguration?.redTransactions ??
        getFeatureConfig(({ base }) => base.showNegativeAmountsInRedColor);
    const shouldComputeColorFromAmount = coloured && !providedColor;

    return shouldComputeColorFromAmount
        ? getAmountColor(amount, showRedTransactions)
        : providedColor;
}
